import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { PaperClipIcon, PaperAirplaneIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { openWhatsAppWebConversation } from '../utils/whatsappWeb';
import { Tip } from './ui/tip';

type ChatClient = {
  id: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  phone?: string;
};

type ChatMessage = {
  id: string;
  type: string;
  text: string;
  caption?: string;
  filename?: string;
  mediaId?: string;
  mediaUrl?: string;
  mimeType?: string;
  direction: 'incoming' | 'outgoing' | string;
  timestamp: string;
  deliveryStatus?: string;
  createdByUserName?: string;
  pending?: boolean;
};

interface ClientWhatsAppChatDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: ChatClient | null;
}

const AUTO_REFRESH_MS = 2000;

const is24hWindowError = (message: string) => {
  const lower = String(message || '').toLowerCase();
  return (
    lower.includes('24-hour window') ||
    lower.includes('outside the 24-hour window') ||
    lower.includes('non-template messages')
  );
};

const ClientWhatsAppChatDialog: React.FC<ClientWhatsAppChatDialogProps> = ({
  open,
  onOpenChange,
  client
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [pendingMessages, setPendingMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFilePreviewUrl, setSelectedFilePreviewUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [lastFetchError, setLastFetchError] = useState('');
  const [mediaObjectUrls, setMediaObjectUrls] = useState<Record<string, string>>({});
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const loadingMediaRef = useRef<Set<string>>(new Set());
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingStreamRef = useRef<MediaStream | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<number | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const clientName = useMemo(() => {
    if (!client) return '';
    if (client.companyName) return client.companyName;
    return [client.firstName, client.lastName].filter(Boolean).join(' ').trim() || 'Cliente';
  }, [client]);

  const scrollToBottom = (instant = false) => {
    const doScroll = () => {
      bottomRef.current?.scrollIntoView({ behavior: instant ? 'instant' as ScrollBehavior : 'smooth' });
    };
    requestAnimationFrame(() => {
      doScroll();
      if (instant) setTimeout(doScroll, 50);
    });
  };

  const isNearBottom = () => {
    const el = messagesContainerRef.current;
    if (!el) return true;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    return remaining <= 48;
  };

  const maybeScrollToBottom = (force = false) => {
    if (force || stickToBottomRef.current) scrollToBottom(force);
  };

  const normalizeText = (value?: string) => String(value || '').trim().toLowerCase();

  const isSameOutgoingMessage = (serverMsg: ChatMessage, pendingMsg: ChatMessage) => {
    if (serverMsg.direction !== 'outgoing') return false;
    if (normalizeText(serverMsg.type) !== normalizeText(pendingMsg.type)) return false;
    if (normalizeText(serverMsg.text) !== normalizeText(pendingMsg.text)) return false;
    if (normalizeText(serverMsg.caption) !== normalizeText(pendingMsg.caption)) return false;
    if (normalizeText(serverMsg.filename) !== normalizeText(pendingMsg.filename)) return false;
    const serverTs = new Date(serverMsg.timestamp).getTime();
    const pendingTs = new Date(pendingMsg.timestamp).getTime();
    if (!Number.isFinite(serverTs) || !Number.isFinite(pendingTs)) return false;
    return Math.abs(serverTs - pendingTs) <= 5 * 60 * 1000;
  };

  const mergeServerAndPending = (serverList: ChatMessage[], pendingList: ChatMessage[]) => {
    const unresolvedPending = pendingList.filter(
      (pending) => !serverList.some((server) => isSameOutgoingMessage(server, pending))
    );
    return [...serverList, ...unresolvedPending].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
  };

  const detectMediaType = (file: File | null) => {
    if (!file) return 'text';
    const mime = String(file.type || '').toLowerCase();
    if (mime.startsWith('image/')) return 'image';
    if (mime.startsWith('audio/')) return 'audio';
    if (mime.startsWith('video/')) return 'video';
    return 'document';
  };

  const fetchMessages = async (silent = false, forceScroll = false) => {
    if (!open || !client?.phone) return;
    if (!silent) setLoading(true);
    try {
      const response = await axios.get('/api/whatsapp/chat/messages', {
        params: { phone: client.phone, limit: 60 }
      });
      const rawList = Array.isArray(response.data?.messages) ? response.data.messages : [];
      const list: ChatMessage[] = rawList as ChatMessage[];
      setPendingMessages((prevPending) => {
        const unresolvedPending = prevPending.filter(
          (pending) => !list.some((server: ChatMessage) => isSameOutgoingMessage(server, pending))
        );
        setMessages(mergeServerAndPending(list, unresolvedPending));
        return unresolvedPending;
      });
      setLastFetchError('');
      maybeScrollToBottom(forceScroll);
    } catch (error: any) {
      const msg = error?.response?.data?.error || 'No se pudo cargar el chat';
      setLastFetchError(msg);
      if (!silent) {
        toast.error(msg);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    stickToBottomRef.current = true;
    fetchMessages(false, true);
    const timer = setInterval(() => {
      fetchMessages(true);
    }, AUTO_REFRESH_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, client?.phone]);

  useEffect(() => {
    if (!open) {
      setMessages([]);
      setPendingMessages([]);
      Object.values(mediaObjectUrls).forEach((url) => {
        try { URL.revokeObjectURL(url); } catch (_e) {}
      });
      setMediaObjectUrls({});
      setText('');
      setSelectedFile(null);
      setSelectedFilePreviewUrl('');
      setSending(false);
      setImagePreviewUrl('');
      setIsRecording(false);
      setRecordingSeconds(0);
      stickToBottomRef.current = true;
      if (recordingTimerRef.current) {
        window.clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (recordingStreamRef.current) {
        recordingStreamRef.current.getTracks().forEach((t) => t.stop());
      }
      recordingStreamRef.current = null;
      mediaRecorderRef.current = null;
    }
  }, [open]);

  const handleMessagesScroll = () => {
    stickToBottomRef.current = isNearBottom();
  };

  useEffect(() => {
    if (!selectedFile) {
      setSelectedFilePreviewUrl('');
      return;
    }
    const t = String(selectedFile.type || '').toLowerCase();
    const canPreview = t.startsWith('image/') || t.startsWith('audio/') || t.startsWith('video/');
    if (!canPreview) {
      setSelectedFilePreviewUrl('');
      return;
    }
    const objectUrl = URL.createObjectURL(selectedFile);
    setSelectedFilePreviewUrl(objectUrl);
    return () => {
      try { URL.revokeObjectURL(objectUrl); } catch (_e) {}
    };
  }, [selectedFile]);

  useEffect(() => {
    if (!open) return;
    const candidates = messages.filter((msg) => {
      const t = String(msg.type || '').toLowerCase();
      const isMediaType = ['image', 'video', 'audio', 'document', 'sticker'].includes(t);
      return isMediaType && (msg.mediaId || msg.mediaUrl) && !mediaObjectUrls[msg.id] && !loadingMediaRef.current.has(msg.id);
    });

    candidates.forEach((msg) => {
      loadingMediaRef.current.add(msg.id);
      axios.get('/api/whatsapp/chat/media', {
        params: {
          mediaId: msg.mediaId || '',
          mediaUrl: msg.mediaUrl || '',
          mimeType: msg.mimeType || ''
        },
        responseType: 'blob'
      })
        .then((resp) => {
          const objectUrl = URL.createObjectURL(resp.data);
          setMediaObjectUrls((prev) => {
            if (prev[msg.id]) {
              try { URL.revokeObjectURL(objectUrl); } catch (_e) {}
              return prev;
            }
            return { ...prev, [msg.id]: objectUrl };
          });
        })
        .catch(() => {
          // ignore single media errors; message bubble will show fallback text
        })
        .finally(() => {
          loadingMediaRef.current.delete(msg.id);
        });
    });
  }, [messages, mediaObjectUrls, open]);

  const handleSend = async (override?: { text?: string; file?: File | null; forceAudio?: boolean }) => {
    if (!client?.phone) {
      toast.error('El cliente no tiene telefono cargado');
      return;
    }

    const currentFile = override?.file !== undefined ? override.file : selectedFile;
    const cleanText = (override?.text !== undefined ? override.text : text).trim();
    if (!currentFile && !cleanText) {
      return;
    }

    const nowIso = new Date().toISOString();
    const optimisticText = cleanText;
    const optimisticFile = currentFile;
    const optimisticId = `tmp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const optimisticType = override?.forceAudio ? 'audio' : detectMediaType(optimisticFile);

    setSending(true);
    const optimisticMessage: ChatMessage = {
      id: optimisticId,
      type: optimisticType,
      text: optimisticText,
      caption: optimisticFile ? optimisticText : '',
      filename: optimisticFile?.name || '',
      direction: 'outgoing',
      timestamp: nowIso,
      pending: true
    };
    setPendingMessages((prev) => {
      const next = [...prev, optimisticMessage];
      setMessages((prevMessages) => mergeServerAndPending(prevMessages.filter((m) => !m.pending), next));
      return next;
    });
    if (override?.text === undefined) setText('');
    if (override?.file === undefined) {
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
    scrollToBottom();

    try {
      if (optimisticFile) {
        const formData = new FormData();
        formData.append('phone', client.phone);
        formData.append('caption', optimisticText);
        const fileMime = String(optimisticFile.type || '').toLowerCase();
        const shouldForceAudio = Boolean(override?.forceAudio) || fileMime.includes('webm');
        if (shouldForceAudio) {
          formData.append('forceAudio', '1');
        }
        formData.append('file', optimisticFile);
        await axios.post('/api/whatsapp/chat/send-media', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axios.post('/api/whatsapp/chat/send-text', {
          phone: client.phone,
          message: optimisticText
        });
      }
      await fetchMessages(true);
      scrollToBottom();
    } catch (error: any) {
      setPendingMessages((prev) => {
        const next = prev.filter((msg) => msg.id !== optimisticId);
        setMessages((prevMessages) => mergeServerAndPending(prevMessages.filter((m) => !m.pending), next));
        return next;
      });
      const errorCode = String(error?.response?.data?.code || '');
      const msg = error?.response?.data?.error || 'No se pudo enviar el mensaje';

      if ((errorCode === 'OUTSIDE_24H_WINDOW' || is24hWindowError(msg)) && client?.phone) {
        openWhatsAppWebConversation(client.phone, error?.response?.data?.whatsappWebUrl);
        toast.error('Ventana de 24h cerrada. Se abri� WhatsApp Web para responder desde ah�.');
        return;
      }

      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const stopRecordingAndSend = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    recorder.stop();
  };

  const startRecording = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
        toast.error('Tu navegador no soporta grabación de audio');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const preferredType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : '';
      const recorder = preferredType ? new MediaRecorder(stream, { mimeType: preferredType }) : new MediaRecorder(stream);
      recordingStreamRef.current = stream;
      mediaRecorderRef.current = recorder;
      recordingChunksRef.current = [];

      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          recordingChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        if (recordingTimerRef.current) {
          window.clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }
        const chunks = recordingChunksRef.current;
        recordingChunksRef.current = [];
        const streamRef = recordingStreamRef.current;
        if (streamRef) {
          streamRef.getTracks().forEach((t) => t.stop());
          recordingStreamRef.current = null;
        }
        setIsRecording(false);
        setRecordingSeconds(0);
        if (!chunks.length) return;
        const mimeType = recorder.mimeType || 'audio/webm';
        const ext = mimeType.includes('ogg') ? 'ogg' : mimeType.includes('mp4') ? 'm4a' : 'webm';
        const audioBlob = new Blob(chunks, { type: mimeType });
        const audioFile = new File([audioBlob], `audio-${Date.now()}.${ext}`, { type: mimeType });
        setSelectedFile(audioFile);
        setText('');
        if (fileInputRef.current) fileInputRef.current.value = '';
        toast.success('Audio grabado. Revisa la vista previa y toca Enviar.');
      };

      recorder.start();
      setIsRecording(true);
      setRecordingSeconds(0);
      if (recordingTimerRef.current) {
        window.clearInterval(recordingTimerRef.current);
      }
      recordingTimerRef.current = window.setInterval(() => {
        setRecordingSeconds((prev) => prev + 1);
      }, 1000);
    } catch (_error) {
      toast.error('No se pudo iniciar la grabación. Revisa permisos de micrófono.');
    }
  };

  const renderMessageContent = (message: ChatMessage) => {
    const t = String(message.type || '').toLowerCase();
    const mediaSrc = mediaObjectUrls[message.id] || '';

    if (t === 'text') {
      return <div>{message.text || ''}</div>;
    }

    if ((t === 'image' || t === 'sticker') && mediaSrc) {
      return (
        <div className="space-y-2">
          <Tip label="Ver imagen en grande">
            <button
              type="button"
              className="block"
              onClick={() => setImagePreviewUrl(mediaSrc)}
            >
              <img src={mediaSrc} alt={message.filename || 'imagen'} className="max-h-64 rounded-md border cursor-zoom-in" />
            </button>
          </Tip>
          {(message.caption || message.text) && <div>{message.caption || message.text}</div>}
        </div>
      );
    }

    if (t === 'video' && mediaSrc) {
      return (
        <div className="space-y-2">
          <video src={mediaSrc} controls className="max-h-64 rounded-md border" />
          {(message.caption || message.text) && <div>{message.caption || message.text}</div>}
        </div>
      );
    }

    if (t === 'audio' && mediaSrc) {
      return (
        <div className="space-y-2">
          <audio src={mediaSrc} controls className="w-64 max-w-full" />
          {(message.caption || message.text) && <div>{message.caption || message.text}</div>}
        </div>
      );
    }

    if (t === 'document' && mediaSrc) {
      return (
        <div className="space-y-2">
          <a
            href={mediaSrc}
            download={message.filename || 'adjunto'}
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            {message.filename || 'Descargar documento'}
          </a>
          {(message.caption || message.text) && <div>{message.caption || message.text}</div>}
        </div>
      );
    }

    const base =
      t === 'image' ? 'Imagen' :
      t === 'video' ? 'Video' :
      t === 'audio' ? 'Audio' :
      t === 'sticker' ? 'Sticker' :
      t === 'document' ? 'Documento' :
      'Adjunto';
    const name = message.filename ? `: ${message.filename}` : '';
    const caption = message.caption ? `\n${message.caption}` : message.text ? `\n${message.text}` : '';
    return <div>{`${base}${name}${caption}`}</div>;
  };

  const renderOutgoingStatus = (message: ChatMessage) => {
    if (message.pending) {
      return <span> - enviando</span>;
    }
    const status = String(message.deliveryStatus || '').toLowerCase();
    if (status === 'read') {
      return <span className="text-sky-400"> ✓✓</span>;
    }
    if (status === 'delivered') {
      return <span className="text-emerald-300"> ✓✓</span>;
    }
    if (status === 'failed') {
      return <span className="text-red-400"> !</span>;
    }
    return <span className="text-emerald-300"> ✓</span>;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{clientName || 'Cliente'}</DialogTitle>
        </DialogHeader>

        <div
          ref={messagesContainerRef}
          onScroll={handleMessagesScroll}
          className="flex-1 min-h-[320px] max-h-[56vh] overflow-y-auto rounded-md border bg-muted/20 p-3 space-y-2"
        >
          {lastFetchError && (
            <div className="text-xs text-amber-400 border border-amber-700/40 bg-amber-900/10 rounded px-2 py-1">
              Historial parcial: {lastFetchError}
            </div>
          )}
          {loading && messages.length === 0 ? (
            <div className="text-sm text-muted-foreground">Cargando mensajes...</div>
          ) : messages.length === 0 ? (
            <div className="text-sm text-muted-foreground">Todavia no hay mensajes para este cliente.</div>
          ) : (
            messages.map((msg) => {
              const outgoing = msg.direction === 'outgoing';
              return (
                <div key={msg.id} className={`flex ${outgoing ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words overflow-hidden ${
                      outgoing ? 'bg-emerald-800 text-emerald-50' : 'bg-zinc-700 text-zinc-100'
                    }`}
                  >
                    {outgoing && msg.createdByUserName && (
                      <div className="text-[11px] font-semibold text-emerald-400 mb-0.5">{msg.createdByUserName}</div>
                    )}
                    {renderMessageContent(msg)}
                    <div className={`mt-1 text-[11px] ${outgoing ? 'text-emerald-300/80' : 'text-zinc-400'}`}>
                      {new Date(msg.timestamp).toLocaleString('es-AR')}
                      {outgoing && renderOutgoingStatus(msg)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>

        <div className="space-y-2">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={(e) => {
              setSelectedFile(e.target.files?.[0] || null);
            }}
          />

          {selectedFile && (
            <div className="rounded-md px-3 py-2 text-sm bg-muted/40 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate">{selectedFile.name}</span>
                <button
                  type="button"
                  className="h-6 w-6 inline-flex items-center justify-center rounded hover:bg-muted text-muted-foreground"
                  onClick={() => {
                    setSelectedFile(null);
                    setSelectedFilePreviewUrl('');
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
              {selectedFilePreviewUrl && selectedFile.type.startsWith('image/') && (
                <button type="button" className="block" onClick={() => setImagePreviewUrl(selectedFilePreviewUrl)}>
                  <img src={selectedFilePreviewUrl} alt={selectedFile.name || 'preview'} className="max-h-48 rounded-md cursor-zoom-in" />
                </button>
              )}
              {selectedFilePreviewUrl && selectedFile.type.startsWith('audio/') && (
                <audio src={selectedFilePreviewUrl} controls className="w-full" />
              )}
              {selectedFilePreviewUrl && selectedFile.type.startsWith('video/') && (
                <video src={selectedFilePreviewUrl} controls className="max-h-56 rounded-md" />
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            <Tip label="Adjuntar archivo">
              <button
                type="button"
                className="h-9 w-9 shrink-0 inline-flex items-center justify-center rounded-full hover:bg-muted text-muted-foreground transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <PaperClipIcon className="h-5 w-5" />
              </button>
            </Tip>

            {isRecording ? (
              <button
                type="button"
                className="flex-1 h-10 rounded-full bg-red-500/10 border border-red-500/30 text-red-400 text-sm flex items-center justify-center gap-2"
                onClick={stopRecordingAndSend}
              >
                <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                Grabando {Math.floor(recordingSeconds / 60).toString().padStart(2, '0')}:{(recordingSeconds % 60).toString().padStart(2, '0')} — toca para detener
              </button>
            ) : (
              <input
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (!sending && (selectedFile || text.trim())) handleSend();
                  }
                }}
                placeholder={selectedFile ? 'Mensaje opcional para el adjunto...' : 'Escribe un mensaje...'}
                className="flex-1 h-10 rounded-full bg-muted/50 border border-border px-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                disabled={isRecording}
              />
            )}

            {text.trim() || selectedFile ? (
              <Tip label="Enviar">
                <button
                  type="button"
                  className="h-9 w-9 shrink-0 inline-flex items-center justify-center rounded-full bg-emerald-600 hover:bg-emerald-500 text-white transition-colors disabled:opacity-50"
                  onClick={() => handleSend()}
                  disabled={sending || (!selectedFile && !text.trim())}
                >
                  <PaperAirplaneIcon className="h-4 w-4" />
                </button>
              </Tip>
            ) : (
              <Tip label="Grabar audio">
                <button
                  type="button"
                  className="h-9 w-9 shrink-0 inline-flex items-center justify-center rounded-full hover:bg-muted text-muted-foreground transition-colors"
                  onClick={startRecording}
                  disabled={sending}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                  <path d="M8.25 4.5a3.75 3.75 0 117.5 0v8.25a3.75 3.75 0 11-7.5 0V4.5z" />
                  <path d="M6 10.5a.75.75 0 01.75.75v1.5a5.25 5.25 0 1010.5 0v-1.5a.75.75 0 011.5 0v1.5a6.751 6.751 0 01-6 6.709v2.291h3a.75.75 0 010 1.5h-7.5a.75.75 0 010-1.5h3v-2.291a6.751 6.751 0 01-6-6.709v-1.5A.75.75 0 016 10.5z" />
                </svg>
                </button>
              </Tip>
            )}
          </div>
        </div>
      </DialogContent>
      {imagePreviewUrl && (
        <div
          className="fixed inset-0 z-[1000] bg-black/85 flex items-center justify-center p-4"
          onClick={() => setImagePreviewUrl('')}
        >
          <img
            src={imagePreviewUrl}
            alt="preview"
            className="max-h-[92vh] max-w-[92vw] object-contain rounded-md"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </Dialog>
  );
};

export default ClientWhatsAppChatDialog;

